/**
 * filterRefsMixin — collects slot-rendered filter component instances into arrays keyed by ref
 * name, in render order (by the filter's v-for index).
 *
 * Why this exists: the list screens render their filter components through `Filters`/`RetainedFilters`
 * dynamic slots (`<template v-for="(f,i) in filters" v-slot:[...]>`). In Vue 3 a `ref` placed on a
 * component inside a dynamic-slot v-for compiles WITHOUT `ref_for`, so Vue does not array-collect it —
 * `this.$refs.<name>` becomes the single (last-patched) instance, not an array. Code written for Vue 2
 * that reads `this.$refs.<name>[0] / [1]` then throws `Cannot set properties of undefined`.
 *
 * Usage in a parent:
 *   - bind `:ref="el => collectFilterRef(`${f.type}_ref`, el, i)"` on the slot `<component>`
 *   - read instances with `getFilterRef(name, ordinal)` (null-safe) or `getFilterRefs(name)` (array)
 *   - assign a prop null-safely with `setFilterRefProp(name, ordinal, prop, value)`
 *
 * The buckets are index-keyed, so re-renders overwrite in place (no duplicates) and Vue's function-ref
 * null callbacks drop unmounted instances (no stale). The `filters` watcher and beforeUnmount give a
 * belt-and-suspenders clear whenever the filter list changes or the screen tears down.
 */
export const filterRefsMixin = {
  created () {
    // non-reactive instance store: never wrap child component instances in a reactive proxy
    this._filterRefSlots = {}
  },
  methods: {
    collectFilterRef (name, el, index) {
      if (!name) return
      const slots = this._filterRefSlots || (this._filterRefSlots = {})
      const bucket = slots[name] || (slots[name] = {})
      if (el) bucket[index] = el
      else delete bucket[index]
    },
    getFilterRefs (name) {
      const bucket = this._filterRefSlots && this._filterRefSlots[name]
      if (!bucket) return []
      return Object.keys(bucket)
        .map(Number)
        .sort((a, b) => a - b)
        .map(k => bucket[k])
        .filter(inst => inst != null)
    },
    getFilterRef (name, ordinal) {
      return this.getFilterRefs(name)[ordinal || 0]
    },
    setFilterRefProp (name, ordinal, prop, value) {
      const inst = this.getFilterRef(name, ordinal)
      if (inst) {
        inst[prop] = value
        return true
      }
      return false
    }
  },
  watch: {
    filters () {
      // filter list changed -> drop stale instances. Function refs re-register the current set on the
      // render that follows this (pre-flush) watcher, so the map ends the tick fully repopulated.
      this._filterRefSlots = {}
    }
  },
  beforeUnmount () {
    this._filterRefSlots = {}
  }
}
